// DB-backed music readers. Source of truth for /music, /music/$slug,
// /music/$slug/song/$song, home page featured section, and AI guide context.
// Public (unauthenticated) endpoints — safe to call from any loader.

import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { resolveReleaseState } from "@/lib/content";
import type {
  Release,
  ReleaseCredit,
  StreamingPlatform,
  TrackListing,
} from "@/lib/content";


type ReleaseRow = Database["public"]["Tables"]["releases"]["Row"];
type SongRow = Database["public"]["Tables"]["songs"]["Row"];

function publicClient() {
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient<Database>(process.env.SUPABASE_URL!, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

// ── Mapping DB rows → public Release / TrackListing shapes used across the UI ──

function mapType(t: string): Release["type"] {
  const s = t.toLowerCase();
  if (s === "album") return "Album";
  if (s === "ep") return "EP";
  if (s === "single") return "Single";
  if (s === "mixtape") return "Mixtape";
  // Meditation/score and future values collapse to Album for public display.
  return "Album";
}

function mapStatus(s: string): Release["status"] {
  if (s === "live") return "released";
  if (s === "coming_soon") return "upcoming";
  // draft, archived, and unknown values render as "in the studio".
  return "in-progress";
}


function toStreamingLinks(v: unknown): Partial<Record<StreamingPlatform, string>> | undefined {
  if (!v || typeof v !== "object" || Array.isArray(v)) return undefined;
  const obj = v as Record<string, unknown>;
  const out: Partial<Record<StreamingPlatform, string>> = {};
  for (const [k, val] of Object.entries(obj)) {
    if (typeof val === "string" && val.trim()) {
      out[k as StreamingPlatform] = val;
    }
  }
  return Object.keys(out).length ? out : undefined;
}

function toCredits(v: unknown): ReleaseCredit[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const out: ReleaseCredit[] = [];
  for (const item of v) {
    if (item && typeof item === "object") {
      const rec = item as Record<string, unknown>;
      if (typeof rec.role === "string" && typeof rec.name === "string") {
        out.push({ role: rec.role, name: rec.name });
      }
    }
  }
  return out.length ? out : undefined;
}

function toHiddenSymbols(v: unknown): TrackListing["hiddenSymbols"] {
  if (!Array.isArray(v)) return undefined;
  const out: NonNullable<TrackListing["hiddenSymbols"]> = [];
  for (const item of v) {
    if (item && typeof item === "object") {
      const rec = item as Record<string, unknown>;
      if (typeof rec.symbol === "string" && typeof rec.meaning === "string") {
        out.push({ symbol: rec.symbol, meaning: rec.meaning });
      }
    }
  }
  return out.length ? out : undefined;
}

function toGallery(v: unknown): TrackListing["gallery"] {
  if (!Array.isArray(v)) return undefined;
  const out: NonNullable<TrackListing["gallery"]> = [];
  for (const item of v) {
    if (item && typeof item === "object") {
      const rec = item as Record<string, unknown>;
      if (typeof rec.url === "string") {
        out.push({ url: rec.url, caption: typeof rec.caption === "string" ? rec.caption : undefined });
      }
    }
  }
  return out.length ? out : undefined;
}

function toWallpapers(v: unknown): TrackListing["wallpapers"] {
  if (!Array.isArray(v)) return undefined;
  const out: NonNullable<TrackListing["wallpapers"]> = [];
  for (const item of v) {
    if (item && typeof item === "object") {
      const rec = item as Record<string, unknown>;
      if (typeof rec.url === "string") {
        out.push({
          url: rec.url,
          label: typeof rec.label === "string" ? rec.label : undefined,
          resolution: typeof rec.resolution === "string" ? rec.resolution : undefined,
        });
      }
    }
  }
  return out.length ? out : undefined;
}

function mapSong(row: SongRow): TrackListing {
  return {
    title: row.title,
    slug: row.slug,
    duration: row.duration ?? undefined,
    audioUrl: row.audio_url ?? undefined,
    lyrics: row.lyrics?.length ? row.lyrics : undefined,
    credits: toCredits(row.credits),
    links: toStreamingLinks(row.links),
    meaning: row.meaning ?? undefined,
    story: row.story ?? undefined,
    hiddenSymbols: toHiddenSymbols(row.hidden_symbols),
    productionNotes: row.production_notes?.length ? row.production_notes : undefined,
    gallery: toGallery(row.gallery),
    wallpapers: toWallpapers((row as unknown as Record<string, unknown>).wallpapers),
  };
}

function mapRelease(row: ReleaseRow, songs: SongRow[] = []): Release {
  // Canonical, date-derived public state — no manual launch-day edit.
  const status = resolveReleaseState({
    status: mapStatus(row.status),
    releaseDate: row.release_date,
    year: row.year,
  });
  const rawPreSave = (row as unknown as Record<string, unknown>).pre_save_url;
  // A released record can never emit pre-save language, even if a stale
  // pre-save URL survives in the database row.
  const preSaveUrl =
    status === "released" || typeof rawPreSave !== "string" || !rawPreSave.trim()
      ? undefined
      : rawPreSave;
  return {
    slug: row.slug,
    title: row.title,
    type: mapType(row.type),
    year: row.year ?? undefined,
    releaseDate: row.release_date ?? undefined,
    runtime: row.runtime ?? undefined,
    statement: row.statement,
    mood: row.mood ?? [],
    frequency: row.frequency ?? "",
    status,

    coverUrl: row.cover_url ?? undefined,
    purchaseUrl: row.purchase_url ?? undefined,
    purchaseLabel: row.purchase_label ?? undefined,
    previewUrl: row.preview_url ?? undefined,
    preSaveUrl,
    links: toStreamingLinks(row.links),
    credits: toCredits(row.credits),
    lyricsExcerpts: row.lyrics_excerpts?.length ? row.lyrics_excerpts : undefined,
    tracks: songs.length ? songs.map(mapSong) : undefined,
  };
}


// ── Server functions ──────────────────────────────────────────────────────

/** All releases, ordered by sort_order. No status filter — every seeded row
 * is publicly visible so the archive matches the historical content.ts view. */
export const getReleases = createServerFn({ method: "GET" }).handler(async () => {
  const supa = publicClient();
  const { data, error } = await supa
    .from("releases")
    .select("*")
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapRelease(r));
});

/** One release + its ordered songs. */
export const getReleaseBySlug = createServerFn({ method: "GET" })
  .inputValidator((raw: unknown) => raw as { slug: string })
  .handler(async ({ data }) => {
    if (!data?.slug) return null;
    const supa = publicClient();
    const [relRes, songsRes] = await Promise.all([
      supa.from("releases").select("*").eq("slug", data.slug).maybeSingle(),
      supa.from("songs").select("*").eq("release_slug", data.slug).order("sort_order", { ascending: true }),
    ]);
    if (relRes.error) throw new Error(relRes.error.message);
    if (songsRes.error) throw new Error(songsRes.error.message);
    if (!relRes.data) return null;
    return mapRelease(relRes.data, songsRes.data ?? []);
  });

/** Ordered songs for a release (used when only songs are needed). */
export const getSongsForRelease = createServerFn({ method: "GET" })
  .inputValidator((raw: unknown) => raw as { releaseSlug: string })
  .handler(async ({ data }) => {
    if (!data?.releaseSlug) return [] as TrackListing[];
    const supa = publicClient();
    const { data: songs, error } = await supa
      .from("songs")
      .select("*")
      .eq("release_slug", data.releaseSlug)
      .order("sort_order", { ascending: true });
    if (error) throw new Error(error.message);
    return (songs ?? []).map(mapSong);
  });

/** One song + its parent release, mirroring the old findSong helper. */
export const findSongDb = createServerFn({ method: "GET" })
  .inputValidator((raw: unknown) => raw as { releaseSlug: string; songSlug: string })
  .handler(async ({ data }) => {
    if (!data?.releaseSlug || !data?.songSlug) return null;
    const supa = publicClient();
    const [relRes, songsRes] = await Promise.all([
      supa.from("releases").select("*").eq("slug", data.releaseSlug).maybeSingle(),
      supa.from("songs").select("*").eq("release_slug", data.releaseSlug).order("sort_order", { ascending: true }),
    ]);
    if (relRes.error) throw new Error(relRes.error.message);
    if (songsRes.error) throw new Error(songsRes.error.message);
    if (!relRes.data) return null;
    const songs = songsRes.data ?? [];
    const release = mapRelease(relRes.data, songs);
    const index = songs.findIndex((s) => s.slug === data.songSlug);
    if (index < 0) return null;
    const track = mapSong(songs[index]);
    return { release, track, index };
  });

/** Homepage featured release: featured=true, else first by sort_order. */
export const getFeaturedRelease = createServerFn({ method: "GET" }).handler(async () => {
  const supa = publicClient();
  const featuredRes = await supa
    .from("releases")
    .select("*")
    .eq("featured", true)
    .order("sort_order", { ascending: true })
    .limit(1)
    .maybeSingle();
  if (featuredRes.error) throw new Error(featuredRes.error.message);
  if (featuredRes.data) return mapRelease(featuredRes.data);
  const firstRes = await supa
    .from("releases")
    .select("*")
    .order("sort_order", { ascending: true })
    .limit(1)
    .maybeSingle();
  if (firstRes.error) throw new Error(firstRes.error.message);
  return firstRes.data ? mapRelease(firstRes.data) : null;
});

